import React from 'react';
import { Tooltip } from '@mui/material';
import Chip from '@components/Atoms/Chip/Chip';

export type Node = {
  id: string;
  label: string;
  children?: Node[];
  noChip?: boolean;
  noCheckbox?: boolean;
  [key: string]: unknown;
};

interface Tag {
  name: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'warning' | 'success';
  tooltip: React.ReactNode;
}

interface SelectedChipsProps {
  nodes: Node[];
  tags: Record<string, Tag>;
  disabled: boolean;
  onDelete: (id: string) => void;
  background?: Record<string, string>;
}

const getNodeLabel = (node: Node, nodeTag?: Tag) => (
  <span
    key={node.id}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    }}
  >
    {!!nodeTag && (
      <Chip
        label={nodeTag.name}
        size="small"
        color={nodeTag.color}
        clickable={false}
        variant="outlined"
        sx={{
          fontSize: '0.625rem',
          height: '1rem',
          backgroundColor: 'white',
          cursor: 'pointer',
          flexShrink: 0,
          '&[role=button]:hover': {
            opacity: 1,
          },
        }}
      />
    )}
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {node.label}
    </span>
  </span>
);

const SelectedChips: React.FC<SelectedChipsProps> = ({
  nodes,
  tags,
  disabled,
  onDelete,
  background = {},
}) => {
  const defaultTag = 'default' in tags ? tags.default : undefined;

  return nodes.map((node) => {
    const nodeTag = node.id in tags ? tags[node.id] : defaultTag;
    const nodeBackground = background[node.id] ?? 'white';

    return (
      <Tooltip
        title={nodeTag?.tooltip || ''}
        key={node.id}
        followCursor
        placement="bottom-start"
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        }}
        slotProps={{
          tooltip: {
            sx: {
              minWidth: '30rem',
              bgcolor: 'white',
              color: 'text.primary',
              backdropFilter: 'blur(4px)',
              borderRadius: '10px',
              padding: '1rem',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #E0E0E0',
              maxWidth: 'none',
              '& .MuiTypography-root': {
                color: 'text.primary',
              },
              '& .MuiBox-root': {
                color: 'text.primary',
              },
            },
          },
        }}
      >
        <span style={{ display: 'block', maxWidth: '100%' }}>
          <Chip
            key={node.id}
            label={getNodeLabel(node, nodeTag)}
            size="small"
            onDelete={disabled ? undefined : () => onDelete(node.id)}
            color="chipGrayCT"
            clickable={false}
            variant="outlined"
            sx={{
              cursor: 'pointer',
              '&[role=button]:hover': {
                opacity: 1,
              },
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              background: nodeBackground,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              },
            }}
          />
        </span>
      </Tooltip>
    );
  });
};

export default SelectedChips;
